import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, ArrowRight, Minus, Linkedin } from "lucide-react";
import Joi from "joi";
import axios from "axios";
import helpers from "../../../lib/crypto";
import confetti from "canvas-confetti";
import { useFunctionContext } from "../../context/FunctionalContext";

const API_URL = import.meta.env.VITE_API_URL;

const Footer = () => {
  const { ORG_ID } = useFunctionContext();

  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [formData, setFormData] = useState({ email: "" });
  const [error, setError] = useState("");

  const handleEmail = (e) => {
    const { name, value } = e.target;
    const formatted = value.replace(/[^a-zA-Z0-9@.]/g, "");
    setFormData({ ...formData, [name]: formatted });

    const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formatted);
    setError(isValid ? "" : "Invalid email");
  };

  const add_leads = (data) => {
    const schema = Joi.object({
      key: Joi.string().required(),
      source: Joi.string().required(),
      email: Joi.string()
        .trim()
        .min(10)
        .max(55)
        .email({ tlds: { allow: false } })
        .required()
        .messages({
          "string.email": "Please enter a valid email address",
        }),
      admin_type: Joi.string().optional().allow("", null),
      organisation_id: Joi.string().required(),
      route_action: Joi.number().valid(1).required(),
      status: Joi.string().trim().valid("New").required(),
      next_follow_up: Joi.date().iso().allow("", null),
    });

    return schema.validate(data);
  };

  const doSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    if (!formData.email) {
      setError("Please fill all the fields");
      setLoading(false);
      return;
    }

    const payload = {
      key: "scanglobal",
      route_action: 1,
      admin_type: "1",
      organisation_id: ORG_ID,
      email: formData.email,
      status: "New",
      source: "Realty",
      next_follow_up: new Date().toISOString(),
    };

    const { error: validationError } = add_leads(payload);
    if (validationError) {
      setError(validationError.details[0].message);
      setLoading(false);
      return;
    }

    let encrypted;
    try {
      encrypted = helpers.encryptobj(payload);
    } catch {
      setError("Encryption failed.");
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(
        `${API_URL}/admin/add_update_lead`,
        { enc: encrypted },
        { headers: { "Content-Type": "application/json" } }
      );

      if (response.status === 200) {
        setSuccessMessage("Your message has been sent successfully!");
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 },
        });
        setError("");
        setFormData({ email: "" });
      } else {
        setError(
          response.data?.message || "Something went wrong on the server"
        );
      }
    } catch (err) {
      console.error("Axios error:", err);
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(""), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  return (
    <footer className="bg-gradient-to-t from-gold/10 to-transparent w-full relative z-10 mb-14 lg:mb-0">
      <div className="container mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Brand section */}
          <div className="col-span-full lg:col-span-1">
            <Link
              className="flex-none text-xl font-semibold text-white focus:outline-hidden focus:opacity-80"
              to="/realty"
              aria-label="Brand"
            >
              <img
                src="/images/realty/realty-logo.png"
                height={150}
                width={150}
                alt="main logo"
                loading="lazy"
                className="relative object-cover"
              />
            </Link>
          </div>

          {/* Explore section */}
          <div>
            <Minus size={20} className="text-gold" />
            <h4 className="font-semibold text-gray-100 text-xl">Quick Links</h4>
            <ul className="space-y-2 mt-3">
              <li>
                <Link
                  href="#"
                  to="/realty/about-us"
                  className="group relative inline-flex items-center text-gray-400 focus:outline-none focus:text-gray-200 transition-colors duration-300 hover:text-gold"
                >
                  <span className="relative">
                    About
                    {/* Animated underline */}
                    <span className="absolute left-0 -bottom-0.5 h-[2px] w-0 bg-gold bg-opacity-25 transition-all duration-300 group-hover:w-full"></span>
                  </span>

                  {/* Hidden arrow, slides in on hover */}
                  <ArrowRight
                    size={13}
                    className="opacity-0 translate-x-0 text-gold group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300"
                  />
                </Link>
              </li>
              <li>
                <Link
                  href="#"
                  to="/realty/listings"
                  className="group relative inline-flex items-center text-gray-400 focus:outline-none focus:text-gray-200 transition-colors duration-300 hover:text-gold"
                >
                  <span className="relative">
                    Listings
                    {/* Animated underline */}
                    <span className="absolute left-0 -bottom-0.5 h-[2px] w-0 bg-gold bg-opacity-25 transition-all duration-300 group-hover:w-full"></span>
                  </span>

                  {/* Hidden arrow, slides in on hover */}
                  <ArrowRight
                    size={13}
                    className="opacity-0 translate-x-0 text-gold group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300"
                  />
                </Link>
              </li>
              <li>
                <Link
                  href="#"
                  to="/realty/blog"
                  className="group relative inline-flex items-center text-gray-400 focus:outline-none focus:text-gray-200 transition-colors duration-300 hover:text-gold"
                >
                  <span className="relative">
                    Blog
                    {/* Animated underline */}
                    <span className="absolute left-0 -bottom-0.5 h-[2px] w-0 bg-gold bg-opacity-25 transition-all duration-300 group-hover:w-full"></span>
                  </span>

                  {/* Hidden arrow, slides in on hover */}
                  <ArrowRight
                    size={13}
                    className="opacity-0 translate-x-0 text-gold group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300"
                  />
                </Link>
              </li>
              <li>
                <Link
                  href="#"
                  to="/realty/contact"
                  className="group relative inline-flex items-center text-gray-400 focus:outline-none focus:text-gray-200 transition-colors duration-300 hover:text-gold"
                >
                  <span className="relative">
                    Contact
                    {/* Animated underline */}
                    <span className="absolute left-0 -bottom-0.5 h-[2px] w-0 bg-gold bg-opacity-25 transition-all duration-300 group-hover:w-full"></span>
                  </span>

                  {/* Hidden arrow, slides in on hover */}
                  <ArrowRight
                    size={13}
                    className="opacity-0 translate-x-0 text-gold group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300"
                  />
                </Link>
              </li>
            </ul>
          </div>

          {/* Visit section */}
          <div>
            <Minus size={20} className="text-gold" />
            <h4 className="font-semibold text-gray-100 text-xl">Visit</h4>
            {/* <address className="not-italic text-cream/80 text-gray-400 mt-3">
              <p className="mt-2 font-bold text-base">
               
                Office 805, Donna Towers, Dubai Silicon Oasis, Dubai, UAE
              </p>
              <p className="mt-2 font-bold text-base">
                Unit 208/209, 3485 Rebecca St, <br />
                Oakville, Ontario, Canada L6L 0H4
              </p>
              <p className="mt-2">admin@scanglobal.me</p>
            </address> */}
            <address className="not-italic text-gray-400 mt-3 space-y-4">
              {/* Dubai Office */}
              <div>
                <p className="font-bold text-base text-gray-600">
                  Dubai Office
                </p>
                <p className="text-sm">
                  Office 805, Donna Towers, <br />
                  Dubai Silicon Oasis, Dubai, UAE
                </p>
              </div>

              {/* Canada Office */}
              <div>
                <p className="font-bold text-base text-gray-600">
                  Canada Office
                </p>
                <p className="text-sm">
                  Unit 208/209, 3485 Rebecca St, <br />
                  Oakville, Ontario, Canada L6L 0H4
                </p>
              </div>

              {/* Contact Email */}
              <div>
                <p className="font-bold text-base text-gray-600">Email</p>
                <a
                  href="mailto:admin@scanglobal.me"
                  className="text-sm text-gold hover:underline"
                >
                  admin@scanglobal.me
                </a>
              </div>
            </address>
          </div>

          {/* Newsletter section */}
          <div className="bg-neutral-900 p-4 rounded-md">
            <Minus size={20} className="text-gold" />
            <h4 className="font-semibold text-gray-100 text-xl">Newsletter</h4>

            <p className="text-cream/80 mb-4 mt-3 text-gray-400">
              Stay updated with our latest collections
            </p>
            <form
              action="#"
              method="POST"
              onSubmit={doSubmit}
              className="flex flex-col gap-2"
            >
              <input
                name="email"
                id="hero-input"
                placeholder="Your email"
                className="bg-slate-800/40 text-white px-4 py-2 rounded focus:outline-none focus:ring-1 focus:ring-gold"
                type="text"
                onChange={handleEmail}
                value={formData.email}
                // maxLength={25}
              />

              <button
                type="submit"
                disabled={loading}
                className="bg-gold text-slate-900 px-4 py-2 rounded hover:bg-gold/90 transition-colors font-medium"
              >
                {loading ? "sending...." : "Subscribe"}
                {loading ? (
                  <span className="h-4 w-4 border-2 border-slate-900 border-t-transparent rounded-full animate-spin"></span>
                ) : (
                  ""
                )}
              </button>
              {/* Feedback messages */}
              {error && (
                <p className="text-sm text-red-400 font-medium text-start">
                  {error}
                </p>
              )}

              {successMessage && (
                <p className="mt-4 text-sm text-green-400 font-medium text-start">
                  {successMessage}
                </p>
              )}
            </form>

            <div className="flex mt-6 space-x-4">
              <a
                href="https://www.instagram.com/scanglobalrealty?igsh=djk0ZHYxbXIwaWdm"
                target="_blank"
                rel="noopener noreferrer"
                className="text-cream duration-300 hover:text-gold inline-block transform transition-all 
               hover:scale-110 hover:rotate-[20deg]
               hover:drop-shadow-[0_0_8px_rgb(197,174,122)]"
              >
                <Instagram size={20} />
              </a>
              <a
                href="https://www.facebook.com/ScanGlobalRealty"
                target="_blank"
                rel="noopener noreferrer"
                className="text-cream duration-300 hover:text-gold inline-block transform transition-all 
               hover:scale-110 hover:rotate-[20deg]
               hover:drop-shadow-[0_0_8px_rgb(197,174,122)]"
              >
                <Facebook size={20} />
              </a>
              <a
                href="https://www.linkedin.com/company/scanglobalrealty/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-cream duration-300 hover:text-gold inline-block transform transition-all 
               hover:scale-110 hover:rotate-[20deg]
               hover:drop-shadow-[0_0_8px_rgb(197,174,122)]"
              >
                <Linkedin size={20} />
              </a>
            </div>
          </div>
        </div>

        <div className="mt-8 py-6 border-t border-slate-300/20 flex flex-col md:flex-row justify-between items-center">
          <p className="text-cream/60 text-sm">
            &copy; {new Date().getFullYear()}{" "}
            <span className="text-gold font-bold">Realty</span>. All rights
            reserved.
          </p>
          <div className="flex mt-4 md:mt-0 space-x-6">
            <a href="#" className="text-sm text-cream/60 mt-2 md:mt-0">
              Privacy Policy
            </a>
            <a href="#" className="text-sm text-cream/60 mt-2 md:mt-0">
              Terms of Service
            </a>
          </div>
        </div>
      </div>
      <div className="bg-[radial-gradient(closest-side,theme(colors.gold),theme(colors.gold),transparent)] h-[3px]"></div>
    </footer>
  );
};

export default Footer;
